import { motion } from 'motion/react'
import clsx from 'clsx'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { LEVELS } from '@/src/game/config/levels'
import { useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'

type EditorButtonProps = {
    levelIndex?: number
    alt: string
    imageClassName?: string
    imageSrc: string
    isActive?: boolean
    isLocked?: boolean
    onClick?: () => void
}

export default function EditorButton({
    levelIndex,
    alt,
    imageClassName,
    imageSrc,
    isActive = false,
    isLocked = false,
    onClick,
}: EditorButtonProps) {

    const isStele = useSteleBreakpoint();
    const [coinTapAnimationKey, setCoinTapAnimationKey] = useState(0)
    const [showRing, setShowRing] = useState(isActive)

    const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.pointerType === 'touch') {
            event.preventDefault()
        }
    }

    const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        if (isLocked) {
            event.preventDefault()
            setCoinTapAnimationKey((current) => current + 1)
            return
        }

        onClick?.()
    }

    return (
        <motion.button
            initial={{ scale: 0 }}
            animate={{
                scale: isActive ? 1.2 : 1,
            }}
            onAnimationStart={() => {
                setShowRing(isActive)
            }}
            whileTap={{ scale: 0.85 }}
            type='button'
            onPointerDown={handlePointerDown}
            onClick={handleClick}
            onPointerUp={() => { }}
            onPointerCancel={() => { }}
            aria-label={alt}
            aria-pressed={isActive}
            aria-disabled={isLocked}
            className={clsx(
                'relative flex justify-center items-center rounded-full shrink-0',
                'w-11 h-11 min-w-11 min-h-11 max-w-11 max-h-11',
                'stele:w-44 stele:h-44 stele:min-w-44 stele:min-h-44 stele:max-w-44 stele:max-h-44',
                !isLocked && 'bg-blur-gradient',
                showRing && 'active-ring'
            )}
            transition={{
                scale: {
                    type: 'spring',
                    stiffness: 420,
                    damping: 22,
                    mass: 0.7,
                },
            }}
        >
            {isLocked ? (
                <>
                    <motion.img
                        src='/images/Bubble_Idle.png'
                        alt=''
                        aria-hidden
                        className='absolute min-w-16 stele:min-w-64 min-h-16 stele:min-h-64'
                        animate={{
                            rotate: [20, -20],
                            scaleX: [0.9, 1.06],
                            scaleY: [1.06, 0.9],
                        }}
                        transition={{
                            duration: 1.75,
                            repeat: Infinity,
                            ease: 'easeInOut',
                            repeatType: 'reverse',
                        }}
                    />
                    <img
                        src={imageSrc}
                        alt=''
                        aria-hidden
                        className={clsx(
                            'absolute opacity-45',
                            'max-w-7 max-h-7',
                            'stele:max-w-28 stele:max-h-28',
                            imageClassName,
                        )}
                    />

                    <motion.div
                        key={coinTapAnimationKey}
                        initial={{ scale: 1 }}
                        animate={{ scale: [1, 1.2, 1.5, 1.2, 1] }}
                        transition={{
                            duration: 0.32,
                            times: [0, 0.2, 0.5, 0.8, 1],
                            ease: 'easeOut',
                        }}
                        className={clsx(
                            'absolute flex justify-center items-center font-bold text-white select-none',
                            'w-10.75 h-10.75 -bottom-3.5 text-sm',
                            'stele:w-43 stele:h-43 stele:text-[64px] stele:-bottom-14',
                        )}
                        aria-hidden
                    >
                        <p
                            className={clsx(
                                'absolute font-grandstander font-black text-yellow-stroke italic',
                                'text-[7px] pr-0.5',
                                'stele:text-[28px] stele:pr-2'
                            )}>
                            {LEVELS[levelIndex ?? 0].end}
                        </p>

                        <img
                            src='/images/CoinNumber.png'
                            className={clsx(
                                'w-6.5 h-7',
                                'min-w-6.5 min-h-7',
                                'stele:w-26 stele:h-28',
                                'stele:min-w-26 stele:min-h-28'
                            )}
                        />
                    </motion.div>

                </>
            ) : (
                <img
                    src={imageSrc}
                    alt={alt}
                    className={clsx(
                        'max-w-10 max-h-10',
                        'stele:max-w-44 stele:max-h-44',
                        imageClassName,
                    )}
                />
            )}
        </motion.button>
    )
}
